import DispatchConstants from 'app/shared/constants/DispatchConstants';
import LoadingConstants from 'app/shared/constants/LoadingConstants';
import BillingConstants from 'app/shared/constants/BillingConstants';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import ErrorMessageConstants from 'app/shared/constants/ErrorMessageConstants';

import format from 'date-fns/format';
import { push } from 'connected-react-router';

import BillingInvoice from 'app/shared/model/BillingInvoice';
import BillingPaymentSessionInfo from 'app/shared/model/BillingPaymentSessionInfo';
import BillingPaymentMethod from 'app/shared/model/BillingPaymentMethod';

import analyticsUtils from 'app/client/utils/analyticsUtils';
import { downloadBase64Data } from 'app/shared/utils/downloadUtils';
import api from 'app/shared/utils/api';


const getFilterItems = filter => filter.value.filter(option => option.isChecked).map(option => option.label);

const calculateFilters = (invoice) => {
    const mapValues = set => Array.from(set).sort().map(item => ({
        label: item,
        isChecked: false,
    }));
    if (!invoice) return {};
    const { invoiceItems } = invoice;
    if (!invoiceItems) return {};
    const filters = {};
    const propertySet = new Set();
    const productSet = new Set();
    const citySet = new Set();
    const stateSet = new Set();
    invoiceItems.forEach((item) => {
        if (item.propertyName) propertySet.add(item.propertyName);
        if (item.chargeName) productSet.add(item.chargeName);
        if (item.city) citySet.add(item.city);
        if (item.state) stateSet.add(item.state);
    });
    if (propertySet.size > 1) {
        filters.propertyName = {
            title: 'Property',
            value: mapValues(propertySet),
            defaultValue: null,
            type: 'MULTISELECT',
        };
    }
    if (productSet.size > 1) {
        filters.chargeName = {
            title: 'Charge Item',
            value: mapValues(productSet),
            defaultValue: null,
            type: 'MULTISELECT',
        };
    }
    if (citySet.size > 1) {
        filters.city = {
            title: 'City',
            value: mapValues(citySet),
            defaultValue: null,
            type: 'MULTISELECT',
        };
    }
    if (stateSet.size > 1) {
        filters.state = {
            title: 'State',
            value: mapValues(stateSet),
            defaultValue: null,
            type: 'MULTISELECT',
        };
    }
    return filters;
};

const createFilters = (queryParams, filters) => {
    for (const [key, filter] of Object.entries(filters)) {
        if (filter.value) {
            if (filter.type === 'DATE') {
                queryParams[key] = format(new Date(filter.value), 'YYYYMMDD');
            } else if (filter.type === 'MULTISELECT') {
                const filterItems = getFilterItems(filter);
                if (filterItems.length) {
                    queryParams[key] = filterItems;
                }
            }
        }
    }
};

const formatInvoice = (invoice) => {
    if (invoice.invoiceItems) {
        invoice.invoiceItems.sort((a, b) => a.propertyName.localeCompare(b.propertyName));
    }
    return invoice;
};

const addNameSpace = (nameSpace, action) => `${nameSpace}${action}`;
const httpTracker = {};

const createBillingListActions = (reducerName, nameSpace) => ({
    fetchPropertyData: () => dispatch => dispatch(api.users.billing.getZuoraBillingFilterOptions())
        .then((result) => {
            if (result.success) {
                dispatch({
                    type: addNameSpace(nameSpace,
                        DispatchConstants.BILLING_SET_MULTIFAMILY_FILTER_OPTIONS),
                    payload: result.response.filters,
                });
            }
            throw new Error();
        })
        .catch(() => {
            // TODO handle error
        }),
    clearInvoices: () => ({
        type: addNameSpace(nameSpace, 'BILLING_CLEAR_INVOICES'),
    }),
    setFilters: filters => (dispatch) => {
        dispatch({
            type: addNameSpace(nameSpace, DispatchConstants.BILLING_SET_FILTERS),
            payload: filters,
        });
        dispatch(
            analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.BILLING_PORTAL_SET_FILTER, {
                label: nameSpace,
            })
        );
    },
    fetchBillingData: () => (dispatch, getState) => {
        const httpTrackTag = ((httpTracker[nameSpace] || 0) + 1) % Number.MAX_SAFE_INTEGER;
        httpTracker[nameSpace] = httpTrackTag;
        const reducer = getState().billing[reducerName];
        const { filters } = reducer;
        const queryParams = {
            hasPayments: nameSpace === BillingConstants.INVOICES_PAID,
            offset: reducer.offset,
        };
        createFilters(queryParams, filters);
        dispatch({
            type: addNameSpace(nameSpace, DispatchConstants.BILLING_SET_LOADING_STATE),
            payload: LoadingConstants.LOADING,
        });
        dispatch(api.users.billing.getInvoices(queryParams))
            .then((result) => {
                if (httpTracker[nameSpace] !== httpTrackTag) return;
                if (!result.success) throw new Error();
                // don't allow non sequential loads
                if (result.response.offset + 1 <= getState().billing[reducerName].offset) return;
                const invoiceData = result.response;
                dispatch({
                    type: addNameSpace(nameSpace, DispatchConstants.BILLING_IMPORT_INVOICES),
                    invoices: invoiceData.invoiceList ? invoiceData.invoiceList.map(BillingInvoice) : [],
                    offset: invoiceData.offset,
                    hasMoreData: invoiceData.hasMoreData || false,
                });
                dispatch({
                    type: addNameSpace(nameSpace, DispatchConstants.BILLING_SET_LOADING_STATE),
                    payload: LoadingConstants.SUCCESS,
                });
            })
            .catch(() => dispatch({
                type: addNameSpace(nameSpace, DispatchConstants.BILLING_SET_LOADING_STATE),
                payload: LoadingConstants.ERROR,
            }));
    },
});

const BillingActions = {
    paymentMethodsInit: () => dispatch => dispatch({
        type: DispatchConstants.BILLING_PAYMENT_METHODS_INIT,
    }),
    getAccountPaymentSettingsForUser: () => (dispatch) => {
        dispatch({
            type: DispatchConstants.BILLING_PAYMENT_METHODS_SET_LOADING_STATE,
            payload: LoadingConstants.LOADING,
        });
        dispatch(api.users.billing.invoicePayment.getAccountPaymentSettingsForUser())
            .then((result) => {
                if (!result.success) throw new Error();
                dispatch({
                    type: DispatchConstants.BILLING_PAYMENT_METHODS_SET_ACCOUNT_SETTINGS,
                    payload: result.response.accountSettings,
                });
            })
            .catch(() => dispatch({
                type: DispatchConstants.BILLING_PAYMENT_METHODS_SET_LOADING_STATE,
                payload: LoadingConstants.ERROR,
            }));
    },
    getPaymentMethodsForUser: () => (dispatch) => {
        dispatch({
            type: DispatchConstants.BILLING_PAYMENT_METHODS_SET_LOADING_STATE,
            payload: LoadingConstants.LOADING,
        });
        dispatch(api.users.billing.invoicePayment.getPaymentMethodsForUser())
            .then((result) => {
                if (!result.success) throw new Error();
                dispatch({
                    type: DispatchConstants.BILLING_PAYMENT_METHODS_SET_PAYMENT_METHODS,
                    payload: result.response.paymentMethods.map(BillingPaymentMethod),
                });
            })
            .catch(() => dispatch({
                type: DispatchConstants.BILLING_PAYMENT_METHODS_SET_LOADING_STATE,
                payload: LoadingConstants.ERROR,
            }));
    },
    accountPaymentMethodsInit: dispatch => dispatch({
        type: DispatchConstants.BILLING_ACCOUNT_PAYMENT_METHODS_INIT,
    }),
    getPaymentMethodsForAccount: accountNumber => (dispatch) => {
        dispatch({
            type: DispatchConstants.BILLING_ACCOUNT_PAYMENT_METHODS_SET_LOADING_STATE,
            payload: LoadingConstants.LOADING,
        });
        return dispatch(api.users.billing.invoicePayment.getPaymentMethodsForAccount({ accountNumber }))
            .then((result) => {
                if (!result.success) throw new Error();
                const paymentMethods = result.response.paymentMethods ?
                    result.response.paymentMethods.map(BillingPaymentMethod) : [];
                dispatch({
                    type: DispatchConstants.BILLING_ACCOUNT_PAYMENT_METHODS_SET_PAYMENT_METHODS,
                    payload: {
                        paymentMethods,
                        paymentMethodLock: result.response.hasPaymentMethodLock,
                        autopayOwnerId: result.response.autopayOwnerId,
                        isAutopayEnabled: result.response.isAutopayEnabled,
                        autopayMethodId: result.response.autopayMethodId,
                        defaultPaymentMethod: result.response.defaultPaymentMethod,
                        accountName: result.response.accountName,
                        canAccessAch: result.response.canAccessAch,
                    },
                });
                return paymentMethods;
            })
            .catch(() => dispatch({
                type: DispatchConstants.BILLING_ACCOUNT_PAYMENT_METHODS_SET_LOADING_STATE,
                payload: LoadingConstants.ERROR,
            }));
    },
    // eslint-disable-next-line max-len
    saveDefaultPaymentMethod: params => dispatch => dispatch(api.users.billing.invoicePayment.saveDefaultPaymentMethod(params))
        .then((result) => {
            if (!result.success) {
                throw new Error();
            }
            return Promise.resolve();
        }).catch(() => Promise.reject(new Error(ErrorMessageConstants.DEFAULT_ERROR))),
    removeAutopay: params => dispatch => dispatch(api.users.billing.invoicePayment.removeAutopay(params))
        .then((result) => {
            if (!result.success) {
                throw new Error();
            }
            return Promise.resolve();
        }).catch(() => Promise.reject(new Error(ErrorMessageConstants.DEFAULT_ERROR))),
    setInvoiceFilters: invoiceFilters => (dispatch) => {
        dispatch(
            analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.BILLING_PORTAL_SET_FILTER, {
                label: 'InvoiceDetailsPage',
            })
        );
        dispatch({
            type: DispatchConstants.BILLING_SET_INVOICE_FILTERS,
            payload: invoiceFilters,
        });
    },
    clearCurrentInvoice: () => (dispatch) => {
        dispatch({
            type: DispatchConstants.BILLING_CLEAR_CURRENT_INVOICE,
            payload: LoadingConstants.INITIAL,
        });
    },
    getInvoice: invoiceNumber => (dispatch) => {
        dispatch({
            type: DispatchConstants.BILLING_SET_CURRENT_INVOICE_ERROR_MESSAGE,
            payload: null,
        });
        dispatch({
            type: DispatchConstants.BILLING_SET_CURRENT_INVOICE_LOADING_STATE,
            payload: LoadingConstants.LOADING,
        });
        dispatch(api.users.billing.getInvoice({ invoiceNumber }))
            .then((result) => {
                if (result.success) {
                    dispatch({
                        type: DispatchConstants.BILLING_IMPORT_INVOICE,
                        payload: {
                            invoice: formatInvoice(BillingInvoice(result.response)),
                            currentInvoiceFilters: calculateFilters(result.response),
                        },
                    });
                } else {
                    dispatch({
                        type: DispatchConstants.BILLING_SET_CURRENT_INVOICE_ERROR_MESSAGE,
                        payload: result.error.message,
                    });
                    dispatch({
                        type: DispatchConstants.BILLING_SET_CURRENT_INVOICE_LOADING_STATE,
                        payload: LoadingConstants.ERROR,
                    });
                }
            })
            .catch(() => {
                dispatch({
                    type: DispatchConstants.BILLING_SET_CURRENT_INVOICE_ERROR_MESSAGE,
                    payload: 'Error loading invoice',
                });
                dispatch({
                    type: DispatchConstants.BILLING_SET_CURRENT_INVOICE_LOADING_STATE,
                    payload: LoadingConstants.ERROR,
                });
            });
    },
    search: invoiceNumber => (dispatch) => {
        if (invoiceNumber) {
            invoiceNumber = invoiceNumber.toUpperCase();
            if (invoiceNumber.match(/^(INV)?\d{8}$/)) {
                if (invoiceNumber.match(/^\d{8}$/)) {
                    invoiceNumber = `INV${invoiceNumber}`;
                }
            } else {
                dispatch({
                    type: DispatchConstants.BILLING_SET_CURRENT_INVOICE_ERROR_MESSAGE,
                    payload: 'Invalid invoice number format.',
                });
                return;
            }
            dispatch({
                type: DispatchConstants.BILLING_SET_CURRENT_INVOICE_LOADING_STATE,
                payload: LoadingConstants.LOADING,
            });
            dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.BILLING_PORTAL_SEARCH));
            dispatch(api.users.billing.getInvoice({ invoiceNumber }))
                .then((result) => {
                    if (!result.success) throw new Error();
                    dispatch({
                        type: DispatchConstants.BILLING_IMPORT_INVOICE,
                        payload: {
                            invoice: result.response,
                            currentInvoiceFilters: calculateFilters(result.response),
                        },
                    });
                    dispatch(push(`/rental-manager/billing/invoice/${invoiceNumber}`));
                })
                .catch(() => {
                    dispatch({
                        type: DispatchConstants.BILLING_SET_CURRENT_INVOICE_ERROR_MESSAGE,
                        payload: `Error getting invoice ${invoiceNumber}`,
                    });
                    dispatch({
                        type: DispatchConstants.BILLING_SET_CURRENT_INVOICE_LOADING_STATE,
                        payload: LoadingConstants.INITIAL,
                    });
                });
        } else {
            dispatch({
                type: DispatchConstants.BILLING_SET_CURRENT_INVOICE_ERROR_MESSAGE,
                payload: '',
            });
            dispatch({
                type: DispatchConstants.BILLING_SET_CURRENT_INVOICE_LOADING_STATE,
                payload: LoadingConstants.INITIAL,
            });
        }
    },
    invoicesDue: createBillingListActions('invoicesDue', BillingConstants.INVOICES_DUE),
    invoicesPaid: createBillingListActions('invoicesPaid', BillingConstants.INVOICES_PAID),
    downloadInvoices: invoiceNumbers => (dispatch) => {
        dispatch({
            type: DispatchConstants.BILLING_SET_INVOICE_DOWNLOAD_ERROR_MESSAGE,
            payload: null,
        });
        dispatch({
            type: DispatchConstants.BILLING_SET_INVOICE_DOWNLOAD_STATE,
            payload: LoadingConstants.LOADING,
        });
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.BILLING_PORTAL_DOWNLOAD_INVOICES));
        return dispatch(api.users.billing.downloadInvoices({ invoiceNumbers }))
            .then((result) => {
                if (!result.success) {
                    dispatch({
                        type: DispatchConstants.BILLING_SET_INVOICE_DOWNLOAD_ERROR_MESSAGE,
                        payload: result.error.message,
                    });
                    dispatch({
                        type: DispatchConstants.BILLING_SET_INVOICE_DOWNLOAD_STATE,
                        payload: LoadingConstants.ERROR,
                    });
                } else {
                    const { data, fileName } = result.response;
                    downloadBase64Data(data, 'application/octet-stream', fileName);
                    dispatch({
                        type: DispatchConstants.BILLING_SET_INVOICE_DOWNLOAD_STATE,
                        payload: LoadingConstants.SUCCESS,
                    });
                }
            })
            .catch(() => {
                dispatch({
                    type: DispatchConstants.BILLING_SET_INVOICE_DOWNLOAD_ERROR_MESSAGE,
                    payload: BillingConstants.DOWNLOAD_ERROR_MESSAGE,
                });
                dispatch({
                    type: DispatchConstants.BILLING_SET_INVOICE_DOWNLOAD_STATE,
                    payload: LoadingConstants.ERROR,
                });
            });
    },
    resetDownloadState: () => (dispatch) => {
        dispatch({
            type: DispatchConstants.BILLING_SET_INVOICE_DOWNLOAD_ERROR_MESSAGE,
            payload: null,
        });
        dispatch({
            type: DispatchConstants.BILLING_SET_INVOICE_DOWNLOAD_STATE,
            payload: LoadingConstants.INITIAL,
        });
    },
    downloadRocketshipCsv: () => (dispatch, getState) => {
        const reducer = getState().billing;
        const { currentInvoice } = reducer.invoice;
        const { invoiceItems } = currentInvoice;
        const hasDiscount = invoiceItems.some(item => Boolean(item.discountPercent));
        let data = 'Listing Source, Contact Email, Contact Phone,';
        data += 'Property address, Unit, City, State, Billable Date Ranges Active,';
        data += 'Days Billed,';
        data += hasDiscount ? 'Discounted Daily Rate,' : 'Amount Per Day,';
        if (hasDiscount) {
            data += 'Market Daily Rate,';
        }
        data += 'Total Amount, Feed Company';
        data += '\n';
        invoiceItems.forEach((item) => {
            const {
                address = '',
                city,
                state,
                quantity,
                chargeAmount,
                unit = '',
                activeRanges,
                feedId,
                contactEmail,
                contactPhone,
                discountPercent,
                feedCompanyName,
            } = item;
            const rate = chargeAmount / quantity;
            const discount = (100 - discountPercent) / 100;
            const originalRate = rate / discount;
            const formattedStreet = address.split(',')[0];
            const formattedUnit = unit.replace(/,/g, '');
            const listingSource = (feedId === 'HotPads' || feedId === 'Postlets') ? 'Zillow' : feedId;
            data += `${listingSource},${contactEmail},${contactPhone},`;
            data += `${formattedStreet},${formattedUnit},${city},${state},`;
            data += `${activeRanges},${quantity},${rate},`;
            if (hasDiscount) {
                data += `${Math.round(originalRate * 100) / 100},`;
            }
            data += `${chargeAmount},${feedCompanyName}`;
            data += '\n';
        });
        downloadBase64Data(btoa(data), 'application/octet-stream', `${currentInvoice.invoiceNumber}.csv`);
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.BILLING_PORTAL_DOWNLOAD_CSV));
    },
    downloadSingleFamilyLegacyCsv: () => (dispatch, getState) => {
        const reducer = getState().billing;
        const { currentInvoice } = reducer.invoice;
        let data = 'Property address, Unit, City, State,';
        data += 'Active Days, Est. Amount, Feed Company\n';
        currentInvoice.invoiceItems.forEach((item) => {
            const {
                address = '',
                city,
                state,
                quantity,
                chargeAmount,
                unit = '',
                feedCompanyName,
            } = item;
            const formattedStreet = address.split(',')[0];
            const formattedUnit = unit.replace(/,/g, '');
            data += `${formattedStreet},${formattedUnit},${city},${state},`;
            data += `${quantity},${chargeAmount},${feedCompanyName}\n`;
        });
        downloadBase64Data(btoa(data), 'application/octet-stream', `${currentInvoice.invoiceNumber}_estimate.csv`);
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.BILLING_PORTAL_DOWNLOAD_CSV));
    },
    getPaymentMethodSessionForAccount: accountNumber => (dispatch) => {
        dispatch({
            type: DispatchConstants.BILLING_SET_PAYMENT_SESSION_INFO_STATE,
            payload: LoadingConstants.LOADING,
        });
        return dispatch(api.users.billing.invoicePayment.getStartPaymentMethodSessionForAccount({ accountNumber }))
            .then((result) => {
                if (!result.success) {
                    dispatch({
                        type: DispatchConstants.BILLING_SET_PAYMENT_SESSION_INFO_STATE,
                        payload: LoadingConstants.ERROR,
                    });
                } else {
                    dispatch({
                        type: DispatchConstants.BILLING_SET_PAYMENT_SESSION_INFO,
                        payload: {
                            paymentSessionInfo: new BillingPaymentSessionInfo(result.response),
                        },
                    });
                }
            })
            .catch(() => {
                dispatch({
                    type: DispatchConstants.BILLING_SET_PAYMENT_SESSION_INFO_STATE,
                    payload: LoadingConstants.ERROR,
                });
            });
    },
    payInvoice: params => (dispatch) => {
        dispatch({
            type: DispatchConstants.BILLING_SET_PAY_INVOICE_STATE,
            payload: LoadingConstants.LOADING,
        });
        return dispatch(api.users.billing.invoicePayment.pay(params))
            .then((result) => {
                if (result.success) {
                    dispatch({
                        type: DispatchConstants.BILLING_SET_PAY_INVOICE_STATE,
                        payload: LoadingConstants.SUCCESS,
                    });
                    return Promise.resolve({});
                }
                dispatch({
                    type: DispatchConstants.BILLING_SET_PAY_INVOICE_STATE,
                    payload: LoadingConstants.ERROR,
                });
                return Promise.reject(new Error(ErrorMessageConstants.DEFAULT_ERROR));
            })
            .catch(() => {
                dispatch({
                    type: DispatchConstants.BILLING_SET_PAY_INVOICE_STATE,
                    payload: LoadingConstants.ERROR,
                });
                dispatch({
                    type: DispatchConstants.BILLING_SET_CURRENT_INVOICE_PAYMENT_IN_PROGRESS,
                    payload: { isPaymentInProgress: false },
                });
                return Promise.reject(new Error(ErrorMessageConstants.DEFAULT_ERROR));
            });
    },
    // eslint-disable-next-line max-len
    importPaymentMethods: accountNumber => dispatch => dispatch(api.users.billing.invoicePayment.importPaymentMethods({ accountNumber })),
    deletePaymentMethod: params => dispatch => dispatch(api.users.billing.invoicePayment.deletePaymentMethod(params)),
    setPaymentMethodLock: paymentMethodLock => ({
        type: DispatchConstants.BILLING_ACCOUNT_PAYMENT_METHODS_SET_PAYMENT_METHOD_LOCK,
        payload: { paymentMethodLock },
    }),
    setIsPaymentInProgress: isPaymentInProgress => ({
        type: DispatchConstants.BILLING_SET_CURRENT_INVOICE_PAYMENT_IN_PROGRESS,
        payload: { isPaymentInProgress },
    }),
    updateInvoicePayment: updatePayload => (dispatch, getState) => {
        const { currentInvoice } = getState().billing.invoice;
        if (updatePayload.accountNumber === currentInvoice.accountNumber &&
            updatePayload.invoiceId === currentInvoice.invoiceId) {
            dispatch(BillingActions.getInvoice(currentInvoice.invoiceNumber));
        }
    },
    setDisplayPaymentSuccess: displaySuccessMessage => ({
        type: DispatchConstants.BILLING_SET_PAYMENT_DISPLAY_SUCCESS,
        payload: { displaySuccessMessage },
    }),
    checkUserPaymentMethods: () => dispatch => dispatch(api.users.billing.invoicePayment.userHasPaymentMethods())
        .then((res) => {
            dispatch({
                type: DispatchConstants.BILLING_USER_SETTINGS_SET_USER_HAS_METHODS,
                payload: res.response.paymentMethodsAvailable,
            });
        })
        .catch(() => {
            // TODO display error
        }),
    initUserSettings: () => ({
        type: DispatchConstants.BILLING_USER_SETTINGS_INIT,
    }),
    setPaymentMethodType: type => ({
        type: DispatchConstants.BILLING_SET_PAYMENT_METHOD_TYPE,
        payload: type,
    }),
};

export default BillingActions;
